import { useEffect, useMemo, useRef, useState } from "react";
import { lotteryService } from "../services/api";
import { uniqueResultDates } from "../services/resultsMapper";
import { buildResultsQuery } from "../services/resultsSearch";

/**
 * Fechas de sorteo para el calendario. Con "Todas" usa los últimos resultados
 * ya cargados; al elegir una lotería trae del backend su propio historial
 * (hasta 200 sorteos), así aparecen fechas antiguas.
 *
 * @returns {{ dates: string[], loading: boolean }}
 */
export default function useResultDates(lotteryId, lotteries, latestResults) {
  const cacheRef = useRef(new Map());
  const [, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lotteryId || cacheRef.current.has(lotteryId)) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    lotteryService
      .listResults(buildResultsQuery({ lotteryId }, lotteries))
      .then((res) => {
        if (cancelled) return;
        if (res.ok) {
          cacheRef.current.set(
            lotteryId,
            uniqueResultDates(res.data, lotteryId, lotteries)
          );
          setVersion((v) => v + 1);
        }
      })
      .catch(() => {
        /* Sin red: se usan las fechas de los últimos resultados. */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lotteryId, lotteries]);

  const cached = lotteryId ? cacheRef.current.get(lotteryId) : undefined;
  const dates = useMemo(() => {
    const base = uniqueResultDates(latestResults, lotteryId, lotteries);
    if (!cached) return base;
    return Array.from(new Set([...base, ...cached])).sort().reverse();
  }, [latestResults, lotteryId, lotteries, cached]);

  return { dates, loading };
}
